import { Engine } from './core/Engine';
import { GameLoop } from './core/GameLoop';
import { Input } from './core/Input';
import { AudioEngine } from './core/Audio';
import { Updatable } from './core/types';
import { TextureLibrary } from './textures/TextureLibrary';
import { Sky } from './world/Sky';
import { Lighting } from './world/Lighting';
import { BuildingRegistry } from './world/BuildingRegistry';
import { CityGrid } from './world/CityGrid';
import { TrafficSystem } from './entities/TrafficSystem';
import { PedestrianSystem } from './entities/PedestrianSystem';
import { BirdFlocks } from './entities/ambient/BirdFlocks';
import { Blimp } from './entities/ambient/Blimp';
import { Balloons } from './entities/ambient/Balloons';
import { NeonBillboards } from './entities/ambient/NeonBillboards';
import { Player } from './player/Player';
import { AABBCollider } from './physics/AABBCollider';
import { HandsRig } from './player/HandsRig';
import { WebSystem } from './player/WebSystem';
import { MovementController } from './player/MovementController';
import { PlayerCamera } from './player/PlayerCamera';
import { Hud } from './ui/Hud';
import { TouchControls } from './ui/TouchControls';
import { isTouchDevice } from './core/device';
import { HALF_CITY } from './config/settings';

const LEFT_BUTTON = 0;
const RIGHT_BUTTON = 2;

/**
 * Composition root: builds every system, wires them together and runs the
 * frame loop. The wiring lives in one place so the dependency graph is obvious;
 * the systems themselves stay decoupled and individually testable.
 */
export class Game {
  private readonly engine: Engine;
  private readonly input: Input;
  private readonly audio = new AudioEngine();
  private readonly hud = new Hud();
  private readonly loop: GameLoop;

  private readonly lighting: Lighting;
  private readonly player = new Player();
  private readonly movement: MovementController;
  private readonly playerCamera: PlayerCamera;
  private readonly web: WebSystem;
  private readonly ambient: Updatable[];

  private readonly isTouch = isTouchDevice();
  private started = false;
  private gaveFirstHint = false;

  constructor(container: HTMLElement) {
    this.engine = new Engine(container);
    this.input = new Input(this.engine.canvas);

    // --- static world ---
    const textures = new TextureLibrary();
    new Sky(this.engine.scene);
    this.lighting = new Lighting(this.engine.scene);
    const registry = new BuildingRegistry();
    new CityGrid(this.engine.scene, textures, registry);

    // --- ambient, living city ---
    this.ambient = [
      new TrafficSystem(this.engine.scene),
      new PedestrianSystem(this.engine.scene),
      new BirdFlocks(this.engine.scene),
      new Blimp(this.engine.scene),
      new Balloons(this.engine.scene),
      new NeonBillboards(this.engine.scene, registry),
    ];

    // --- player ---
    const collider = new AABBCollider(registry);
    const hands = new HandsRig(this.engine.hudScene, this.engine.camera);
    this.web = new WebSystem(this.engine.scene, registry, this.engine.camera, hands, this.input, this.audio, this.player);
    this.movement = new MovementController(this.player, this.input, collider, this.web);
    this.playerCamera = new PlayerCamera(this.engine.camera, this.player);

    if (this.isTouch) {
      new TouchControls(this.input);
      this.hud.enableTouchMode();
    }

    this.wireInput();
    this.loop = new GameLoop((dt) => this.frame(dt));
  }

  start(): void {
    this.hud.markReady();
    this.loop.start();
  }

  private wireInput(): void {
    this.input.onPointerDown((button) => {
      if (button === LEFT_BUTTON) this.web.shoot('L');
      else if (button === RIGHT_BUTTON) this.web.shoot('R');
    });
    this.input.onPointerUp((button) => {
      if (button === LEFT_BUTTON) this.web.release('L');
      else if (button === RIGHT_BUTTON) this.web.release('R');
    });
    this.web.onStateChange((side, active) => this.hud.setWebActive(side, active));
    this.movement.onClimbStart(() =>
      this.hud.showHint('🕷️ Climbing! W/S up·down · A/D sideways · Space to leap off'),
    );
    this.hud.onPlay(() => this.beginSession());
    this.input.onLockChange((locked) => this.onPointerLockChange(locked));
  }

  /** Touch devices can't use pointer lock, so start a synthetic session. */
  private beginSession(): void {
    if (this.isTouch) this.input.activateTouchSession();
    else this.input.requestPointerLock();
  }

  private onPointerLockChange(locked: boolean): void {
    this.hud.setPointerLocked(locked);
    if (locked) {
      this.started = true;
      this.audio.resume();
      this.hud.hideOverlay();
      if (!this.gaveFirstHint) {
        this.hud.showHint(
          this.isTouch
            ? 'Drag to aim at a building, then tap a 🕸️ button to sling'
            : 'Aim at a building and click to sling a web',
        );
        this.gaveFirstHint = true;
      }
    } else if (this.started) {
      this.hud.showPaused();
    }
  }

  private frame(dt: number): void {
    if (this.started) this.simulate(dt);
    else this.showIdleCamera();
    this.hud.update(dt);
    this.engine.render();
  }

  private simulate(dt: number): void {
    this.movement.update(dt);
    this.playerCamera.update(dt);
    this.web.update(dt); // animate hands + redraw web lines (after camera moves)
    this.lighting.followTarget(this.player.position);
    for (const system of this.ambient) system.update(dt);
    this.updateHud();
  }

  private updateHud(): void {
    const kmh = Math.round(this.player.velocity.length() * 3.6 * 2.2); // playful scaling
    this.hud.setSpeed(kmh);
    this.hud.setStats(Math.round(this.player.position.y), this.describeMode());
  }

  private describeMode(): string {
    if (this.web.isSwinging) return 'swinging';
    if (this.movement.stateName === 'climbing') return '🕷️ climbing';
    if (this.movement.stateName === 'airborne') return 'airborne';
    return '';
  }

  private showIdleCamera(): void {
    this.engine.camera.position.set(0, 90, HALF_CITY - 10);
    this.engine.camera.lookAt(0, 60, 0);
  }
}
